/**
 * Round to 1 decimal place (matching Python behavior).
 */
function round1(value) {
  return Math.round(value * 10) / 10;
}

/**
 * Tanner scale keys for graph data.
 * Order: Females T5-T1, then Males T5-T1 (matching Python).
 */
const KEYS = [
  "T5_F", "T4_F", "T3_F", "T2_F", "T1_F", "T5_M", "T4_M", "T3_M", "T2_M", "T1_M"
];

const KEYMAP = {
  "t5f": "T5_F",
  "t4f": "T4_F",
  "t3f": "T3_F",
  "t2f": "T2_F",
  "t1f": "T1_F",
  "t5m": "T5_M",
  "t4m": "T4_M",
  "t3m": "T3_M",
  "t2m": "T2_M",
  "t1m": "T1_M"
};

function makeRawDataField(clientUvf, key, uvf, controlUvf) {
  let data = {};
  try {
    data[uvf] = round1(clientUvf[key][uvf]);
    data[`ctrl-${uvf}`] = round1(controlUvf[key][uvf].AVG);
    // Error bar = AVG * STD_DEV (STD_DEV is stored as coefficient of variation)
    data[`ctrl-${uvf}-Error`] = round1(controlUvf[key][uvf].AVG * controlUvf[key][uvf].STD_DEV);
  } catch (e) {
    data[uvf] = 0;
    data[`ctrl-${uvf}`] = 0;
    data[`ctrl-${uvf}-Error`] = 0;
  }
  return data;
}


/**
 * Separate function to handle new style evaluations.
 **/
function chartDataNewStyleEval(selected, master) {
  let data = Object.keys(selected.uvf).map((key) => {
    let tannerMap = { name: key };

    // Selected.uvf is getting the uvf values given the tanner scale id t1f
    selected.uvf[key].forEach((k, v) => {
      tannerMap[k] = v;
      tannerMap[`ctrl-${k}`] = master.Stim.UVF[key][v].AVG;
    });
  });
}


export function makeChartDataV2({ evaluation, master }) {
  // First, calculate the tanner maps.

  let raw = [];

  // First we build the raw data.
  Object.keys(KEYMAP).forEach((label) => {
    // Skip the control and overall results for building the graphs.
    if (label === 'control' || label === 'overall') {
      return;
    }

    // In building a piece of this map, I need the client uvf key
    //   this means: t1f > sexualized, (sexualized is the uvf).
    //   and I'll need the master uvf key and std_dev.
    //
    //
    //   Since the label at this point is a definite tanner value, we can use
    //   everything under it to build our fields.
    //
    // Start by building a new map for the update.
    let data = { name: label }; // Start a new map with tanner label.
    let currentTannerLabel = evaluation.uvf[label];
    let masterUvf = master.stim.uvf[label];

    // Build the update
    for (let region of Object.keys(evaluation.uvf[label])) {
      try {
        data[region] = round1(currentTannerLabel[region]);
        data[`ctrl-${region}`] = round1(masterUvf[region].avg);
        // Error bar = avg * std_dev (std_dev is stored as coefficient of variation)
        data[`ctrl-${region}-Error`] = round1(masterUvf[region].avg * masterUvf[region].std_dev);
      } catch (e) {
        data[region] = 0;
        data[`ctrl-${region}`] = 0;
        data[`ctrl-${region}-Error`] = 0;
      }
    }

    // Add the update to the raw data.
    raw.push(data);
  });

  // Push the data for each of the error fields.
  raw.push(
    {
      name: "AOI_p",
      image: round1(evaluation.uvf.overall['not-on-roi']),
      "ctrl-image": round1(master.stim.uvf.not_on_roi.avg),
      "ctrl-image-Error": round1(master.stim.uvf.not_on_roi.avg * master.stim.uvf.not_on_roi.std_dev)
    },
    {
      name: "AOI_np",
      image: round1(evaluation.uvf.control['not-on-roi']),
      "ctrl-image": round1(master.control.uvf.not_on_roi.avg),
      "ctrl-image-Error": round1(master.control.uvf.not_on_roi.avg * master.control.uvf.not_on_roi.std_dev)
    },
    {
      name: "err_p",
      image: round1(evaluation.uvf.overall['invalid']),
      "ctrl-image": round1(master.stim.uvf.invalid.avg),
      "ctrl-image-Error": round1(master.stim.uvf.invalid.avg * master.stim.uvf.invalid.std_dev)
    },
    {
      name: "err_np",
      image: round1(evaluation.uvf.control['invalid']),
      "ctrl-image": round1(master.control.uvf.invalid.avg),
      "ctrl-image-Error": round1(master.control.uvf.invalid.avg * master.control.uvf.invalid.std_dev)
    }
  );

  // Now we build the normalized data.
  let normalized = [];
  Object.keys(KEYMAP).forEach((label) => {
    if (label == 'control' || label == 'overall') {
      return;
    }

    let data = { name: label };
    let currentTannerLabel = evaluation.uvf[label];
    let masterUvf = master.stim.uvf[label];

    Object.keys(currentTannerLabel).forEach((region) => {
      // Build the update
      //
      // The calculations are now divided by the mean average region, to normalize
      // control to 1. This makes for a prettier graph!
      try {
        data[region] = round1(currentTannerLabel[region] / masterUvf[region].avg);
        data[`ctrl-${region}`] = 1;
        data[`ctrl-${region}-Error`] = round1(masterUvf[region].std_dev);
      } catch (e) {
        data[region] = 0;
        data[`ctrl-${region}`] = 1;
        data[`ctrl-${region}-Error`] = 0;
      }
    });

    normalized.push(data);
  });

  const masterControl = master.control

  normalized.push(
    {
      name: "AOI_p",
      Image: round1(evaluation.uvf.overall['not-on-roi'] / master.stim.uvf.not_on_roi.avg),
      "ctrl-image": 1,
      "ctrl-image-Error": round1(master.stim.uvf.not_on_roi.std_dev)
    },
    {
      name: "AOI_np",
      image: round1(evaluation.uvf.control['not-on-roi'] / master.control.uvf.not_on_roi.avg),
      "ctrl-image": 1,
      "ctrl-image-Error": round1(master.control.uvf.not_on_roi.std_dev)
    },
    {
      name: "err_p",
      image: round1(evaluation.uvf.overall['invalid'] / master.stim.uvf.invalid.avg),
      "ctrl-image": 1,
      "ctrl-image-Error": round1(master.stim.uvf.invalid.std_dev)
    },
    {
      name: "err_np",
      image: round1(evaluation.uvf.control.invalid / master.control.uvf.invalid.avg),
      "ctrl-image": 1,
      "ctrl-image-Error": round1(master.control.uvf.invalid.std_dev)
    },
  );

  // Why sort before we return? We want to ease the display of the values in the charts.
  return [raw, normalized];
}

/**
 * Make the data that we expect for ReChart.
 *
 * selectedCase will be the full eval result document.
 **/
export function makeChartData({ selectedCase, master }) {

  // TODO: This needs to take into account old and new evals.
  let unitsVisualFixation = selectedCase.Stim.UVF;

  const masterUvf = master.Stim.UVF;
  // Calculate the non-normalized data
  let data = []
  KEYS.map((key) => {
    let tannerMap = { name: key };
    Object.keys(unitsVisualFixation[key]).forEach((uvf) => {
      tannerMap = { ...tannerMap, ...makeRawDataField(unitsVisualFixation, key, uvf, masterUvf) };
    });
    data.push(tannerMap);
  });

  // Add the error fields
  // TODO: This is also presupposing a structure. Fix it to handle new and old evals.

  data.push(
    {
      name: "AOI_p",
      Image: round1(unitsVisualFixation.NotOnROI),
      "ctrl-Image": round1(masterUvf.NotOnROI.AVG),
      "ctrl-Image-Error": round1(masterUvf.NotOnROI.AVG * masterUvf.NotOnROI.STD_DEV)
    },
    {
      name: "AOI_np",
      Image: round1(selectedCase.Control.UVF.NotOnROI),
      "ctrl-Image": round1(master.Control.UVF.NotOnROI.AVG),
      "ctrl-Image-Error": round1(master.Control.UVF.NotOnROI.AVG * master.Control.UVF.NotOnROI.STD_DEV)
    },
    {
      name: "err_p",
      Image: round1(unitsVisualFixation.Invalid),
      "ctrl-Image": round1(masterUvf.Invalid.AVG),
      "ctrl-Image-Error": round1(masterUvf.Invalid.AVG * masterUvf.Invalid.STD_DEV)
    },
    {
      name: "err_np",
      Image: round1(selectedCase.Control.UVF.Invalid),
      "ctrl-Image": round1(master.Control.UVF.Invalid.AVG),
      "ctrl-Image-Error": round1(master.Control.UVF.Invalid.AVG * master.Control.UVF.Invalid.STD_DEV)
    }
  );

  let normalized = [];
  KEYS.map((key) => {
    let tannerMap = { name: key };
    Object.keys(unitsVisualFixation[key]).forEach((uvf) => {
      try {
        tannerMap[uvf] = round1(unitsVisualFixation[key][uvf] / masterUvf[key][uvf].AVG);
        tannerMap[`ctrl-${uvf}`] = 1;
        tannerMap[`ctrl-${uvf}-Error`] = round1(masterUvf[key][uvf].STD_DEV);
      } catch (e) {
        tannerMap[uvf] = 0;
        tannerMap[`ctrl-${uvf}`] = 1;
        tannerMap[`ctrl-${uvf}-Error`] = 0;
      }
    });
    normalized.push(tannerMap);
  });

  const masterControl = master.Control;
  normalized.push(
    {
      name: "AOI_p",
      Image: round1(unitsVisualFixation.NotOnROI / masterUvf.NotOnROI.AVG),
      "ctrl-Image": 1,
      "ctrl-Image-Error": round1(masterUvf.NotOnROI.STD_DEV)
    },
    {
      name: "AOI_np",
      Image: round1(selectedCase.Control.UVF.NotOnROI / masterControl.UVF.NotOnROI.AVG),
      "ctrl-Image": 1,
      "ctrl-Image-Error": round1(master.Control.UVF.NotOnROI.STD_DEV)
    },
    {
      name: "err_p",
      Image: round1(unitsVisualFixation["Invalid"] / masterUvf.Invalid.AVG),
      "ctrl-Image": 1,
      "ctrl-Image-Error": round1(master.Stim.UVF.Invalid.STD_DEV)
    },
    {
      name: "err_np",
      Image: round1(selectedCase.Control.UVF.Invalid / masterControl.UVF.Invalid.AVG),
      "ctrl-Image": 1,
      "ctrl-Image-Error": round1(master.Control.UVF.Invalid.STD_DEV)
    }
  );


  return [data, normalized];
}

/**
 * DA from a Normalized chart row already built by makeChartData.
 * CloudEvalAnalysis plots row.Image against 1 ± row['ctrl-Image-Error']
 * (that error slot is Firestore sd). Do not re-derive client/avg/sd here.
 */
export function scoreDaFromNormalizedRow(row) {
  const client = Number(row?.Image);
  const halfWidth = Number(row?.['ctrl-Image-Error']);
  if (!Number.isFinite(client) || !Number.isFinite(halfWidth)) return null;
  if (client > 1 + halfWidth) return 3;
  if (client < 1 - halfWidth) return 1;
  return 2;
}

export function findNormalizedRow(normalizedData, tannerKey) {
  if (!Array.isArray(normalizedData)) return null;
  return normalizedData.find((row) => row.name === tannerKey) ?? null;
}
